<!-- Product Detail Page: hero, key figures, prices, providers and actions, each in its own card -->
<div class="product-detail-page">
	<app-spinner [loading]="loadingDetails">
		<!-- Hero: platform icon in its brand colour, name, platform and active provider -->
		<section class="pd-card pd-hero">
			<div class="pd-hero-icon" [style.--brand]="platformColor">
				@if (platformIcon; as brand) {
					<app-brand-icon [name]="brand"></app-brand-icon>
				} @else {
					<lucide-icon name="Globe" class="icon-em"></lucide-icon>
				}
			</div>
			<div class="pd-hero-text">
				<h2 class="pd-name">{{ product?.name || '-' }}</h2>
				<div class="pd-hero-meta">
					<span class="pd-chip">{{ platformLabel || '-' }}</span>
					<span class="pd-provider">
						<span class="pd-dot" [class.pd-dot--on]="!!product?.activeProvider"></span>
						{{ getActiveProviderName() }}
						@if (getActiveProviderCode() !== '-') {
							<span class="pd-code">· {{ getActiveProviderCode() }}</span>
						}
					</span>
				</div>
			</div>
		</section>

		<!-- Key figures (read) / the same fields as a form (edit) -->
		<section class="pd-card">
			@if (isEditing) {
				<form [formGroup]="editForm" class="pd-form">
					<app-select
						formControlName="platformId"
						[label]="'products.detail.fields.platform' | transloco"
						[placeholder]="'productsCrud.fields.platformPlaceholder' | transloco"
						[required]="true"
						[options]="platforms"
						size="medium"
						[appendTo]="null"
					></app-select>
					<div class="pd-form-pair">
						<app-input
							formControlName="minAmount"
						[error]="isFieldInvalid('minAmount')"
						[errorMessage]="getErrorMessage('minAmount')"
							type="number"
							[label]="'products.detail.fields.minAmount' | transloco"
							size="medium"
						></app-input>
						<app-input
							formControlName="maxAmount"
						[error]="isFieldInvalid('maxAmount')"
						[errorMessage]="getErrorMessage('maxAmount')"
							type="number"
							[label]="'products.detail.fields.maxAmount' | transloco"
							size="medium"
						></app-input>
					</div>
					<app-input
						formControlName="averageTime"
						[error]="isFieldInvalid('averageTime')"
						[errorMessage]="getErrorMessage('averageTime')"
						type="number"
						[label]="'products.detail.fields.averageTime' | transloco"
						size="medium"
					></app-input>
					<app-checkbox
						formControlName="requiresCustomComments"
						[label]="'products.detail.fields.requiresCustomComments' | transloco"
						size="large"
					></app-checkbox>
				</form>
			} @else {
				<div class="pd-stats">
					<div class="pd-stat">
						<span class="pd-stat-label">{{ 'products.detail.fields.minAmount' | transloco }}</span>
						<span class="pd-stat-value">{{ (product?.minAmount | appNumber) || '-' }}</span>
					</div>
					<div class="pd-stat">
						<span class="pd-stat-label">{{ 'products.detail.fields.maxAmount' | transloco }}</span>
						<span class="pd-stat-value">{{ (product?.maxAmount | appNumber) || '-' }}</span>
					</div>
					<div class="pd-stat">
						<span class="pd-stat-label">{{ 'products.detail.fields.averageTime' | transloco }}</span>
						<span class="pd-stat-value">{{ formatTime(product?.averageTime) }}</span>
					</div>
					<div class="pd-stat">
						<span class="pd-stat-label">{{ 'products.detail.fields.requiresCustomComments' | transloco }}</span>
						<span class="pd-stat-value">
							{{ product?.requiresCustomComments ? ('products.detail.fields.yes' | transloco) : ('products.detail.fields.no' | transloco) }}
						</span>
					</div>
				</div>
			}
		</section>

		<!-- Amounts and prices: one row per tier -->
		<section class="pd-card">
			<h3 class="pd-card-title">{{ 'productsCrud.detail.amountsAndPrices' | transloco }}</h3>
			@if (product?.amounts?.length && product?.prices?.length) {
				<ul class="pd-tiers">
					@for (amount of product!.amounts; track $index) {
						<li class="pd-tier">
							<span class="pd-tier-amount">{{ amount | appNumber }}</span>
							<span class="pd-tier-price">{{ product!.prices[$index] | appCurrency: '1.0-2' }}</span>
						</li>
					}
				</ul>
			} @else {
				<p class="pd-empty">-</p>
			}
		</section>

		<!-- Providers: the active one outlined; outside edit mode, tapping another makes it active
		     right away (after a confirmation): it is not part of the edit form, Cancel cannot undo it -->
		<section class="pd-card">
			<h3 class="pd-card-title">{{ 'productsCrud.detail.providers' | transloco }}</h3>
			@if (!isEditing && getInactiveProviders().length) {
				<p class="pd-hint">{{ 'productsCrud.detail.setActiveHint' | transloco }}</p>
			}
			@if (product?.providers?.length) {
				<div class="pd-providers" [class.loading]="changingProvider">
					@if (changingProvider) {
						<div class="pd-providers-loading">
							<lucide-icon name="LoaderCircle" class="icon-em pd-spin"></lucide-icon>
						</div>
					}
					@for (provider of product!.providers; track provider.id) {
						<div
							class="pd-provider-item"
							[class.active]="provider.isActive"
							[class.clickable]="!isEditing && !provider.isActive && !changingProvider"
							(click)="!provider.isActive && confirmSetActiveProvider(provider)"
						>
							<span class="pd-dot" [class.pd-dot--on]="provider.isActive"></span>
							<div class="pd-provider-info">
								<span class="pd-provider-name">{{ provider.providerName || ('productsCrud.fields.provider' | transloco) }}</span>
								<span class="pd-provider-code">{{ 'productsCrud.detail.code' | transloco }} {{ provider.code }}</span>
							</div>
							@if (provider.isActive) {
								<span class="pd-badge">{{ 'productsCrud.detail.activeBadge' | transloco }}</span>
							}
						</div>
					}
				</div>
			} @else {
				<p class="pd-empty">{{ 'products.detail.fields.noInactiveProviders' | transloco }}</p>
			}
		</section>
	</app-spinner>

	<!-- Actions: main one full width, delete as a quiet red button below -->
	<div class="pd-actions">
		@if (!isEditing) {
			<app-button
				[label]="'products.detail.actions.edit' | transloco"
				icon="Pencil"
				variant="primary"
				(onClick)="onEdit()"
				size="medium"
				class="pd-action-main"
			></app-button>
			<button type="button" class="pd-delete" (click)="onDelete()">
				<lucide-icon name="Trash2" [size]="18"></lucide-icon>
				{{ 'products.detail.actions.delete' | transloco }}
			</button>
		} @else {
			<app-button
				[label]="'common.actions.save' | transloco"
				variant="primary"
				(onClick)="onSave()"
				[loading]="saving"
				[disabled]="saving"
				size="medium"
				class="pd-action-main"
			></app-button>
			<button type="button" class="pd-cancel" [disabled]="saving" (click)="onCancelEdit()">
				{{ 'common.actions.cancel' | transloco }}
			</button>
		}
	</div>
</div>
